import { action, observable, computed } from "mobx";
import PhotoEditingPhoto from "./PhotoEditingPhoto";
import Location from "~/constants/PhotoEditing/Location";
import TimeOfDay from "~/constants/PhotoEditing/TimeOfDay";

let lastTransientId = 0;

const generateNextUUID = () => {
  lastTransientId += 1;

  return `new_${lastTransientId}`;
};

const SortPhotos = (photo1, photo2) => {
  if (photo1.sequence < photo2.sequence) {
    return -1;
  }
  if (photo1.sequence > photo2.sequence) {
    return 1;
  }

  return 0;
};

class PhotoEditingImage {
  /**
   * @property {number|null}
   */
  @observable photoEditingImageId = null;

  /**
   * @property {number}
   */
  @observable location = 0;

  /**
   * @property {number}
   */
  @observable timeOfDay = 0;

  /**
   * @property {string|null}
   */
  @observable url = null;

  /**
   * @property {string|null}
   */
  @observable thumbnailUrl = null;

  /**
   * @property {Array}
   */
  @observable photoEditingPhotos = [];

  /**
   * @property {Array}
   */
  @observable messages = [];

  /**
   * @property {number}
   */
  @observable refundDts = null;

  constructor({
    photoEditingImageId = null,
    photoEditingPhotos = [],
    location,
    timeOfDay,
    url = null,
    thumbnailUrl = null,
    messages = [],
    refundDts = null,
  }) {
    this.photoEditingImageId = photoEditingImageId || generateNextUUID();
    this.location = location;
    this.timeOfDay = timeOfDay;
    this.url = url;
    this.thumbnailUrl = thumbnailUrl;
    this.refundDts = refundDts;

    photoEditingPhotos.sort(SortPhotos);

    photoEditingPhotos.forEach(photoEditingPhoto =>
      this.photoEditingPhotos.push(new PhotoEditingPhoto(photoEditingPhoto)),
    );

    if (messages !== null) {
      messages.forEach(message => this.messages.push(message));
    }
  }

  @computed
  get isTransient() {
    return String(this.photoEditingImageId).indexOf("new_") !== -1;
  }

  @computed
  get isReady() {
    return this.url !== null;
  }

  @computed
  get timeOfDayLabel() {
    switch (this.timeOfDay) {
      case 0:
        return "Day Time";
      // no break
      case 1:
        return "Night Time";
      // no break
      default:
        throw new Error(`Invalid time of day value: ${this.timeOfDay}`);
    }
  }

  @computed
  get locationLabel() {
    switch (this.location) {
      case 0:
        return "Interior";
      // no break
      case 1:
        return "Exterior";
      // no break
      default:
        throw new Error(`Invalid location value: ${this.location}`);
    }
  }

  @action
  toggleTimeOfDay() {
    this.timeOfDay = this.timeOfDay === TimeOfDay.DAY_TIME ? TimeOfDay.NIGHT_TIME : TimeOfDay.DAY_TIME;
  }

  @action
  toggleLocation() {
    this.location = this.location === Location.INTERIOR ? Location.EXTERIOR : Location.INTERIOR;
  }

  @action
  removePhotoAtIndex(index) {
    this.photoEditingPhotos.splice(index, 1);
  }

  @action
  addPhotoAtIndex(photoEditingPhoto, index) {
    photoEditingPhoto.setPhotoEditingImageId(this.photoEditingImageId);
    this.photoEditingPhotos.splice(index, 0, photoEditingPhoto);
  }

  @action
  syncPhotosSequence() {
    this.photoEditingPhotos.forEach((photoEditingPhoto, index) => photoEditingPhoto.setSequence(index));
  }
}

export default PhotoEditingImage;
